import React, { useState } from "react";

import { CreateBuyerListForm } from "components/buyers/CreateBuyerListForm";
import toast from "components/ui/toast/Toast";
import { useAddBuyerEntries } from "lib/hooks/api/buyer_lists/useAddBuyerEntries";
import {
  BuyerList,
  CategorizedBuyerLists,
  useBuyerLists,
} from "lib/hooks/api/buyer_lists/useBuyerLists";
import { useCreateBuyerList } from "lib/hooks/api/buyer_lists/useCreateBuyerList";
import { useRemoveBuyerEntries } from "lib/hooks/api/buyer_lists/useRemoveBuyerEntries";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { routes } from "lib/routes";
import { EventNames, useTracking } from "lib/tracking";
import { BuyerListSharingTypeEnum } from "lib/types/graphQLEnums";
import { isArrayInArray, pluralise } from "../../../../lib/utils";
import { ToastAction } from "../../ToastAction";
import { CreateListFormProps, Item, SaveToList } from "./SaveToList";

export function SaveToBuyerList({
  buyerIds,
  isCreateFormOpen,
  onToggleCreateForm,
}: {
  buyerIds: string[];
  isCreateFormOpen: boolean;
  onToggleCreateForm: (isCreateFormOpen: boolean) => void;
}) {
  const { logEvent } = useTracking();
  const { data: currentUser } = useCurrentUser();

  const {
    data: allBuyerLists,
    isLoading,
    isError,
  } = useBuyerLists("create_buyer_list_entries", {
    enabled: !!currentUser,
  });

  const [searchText, setSearchText] = useState("");
  const [savingItemId, setSavingItemId] = useState<string | undefined>();

  const { mutate: addBuyerToList } = useAddBuyerEntries({
    onSuccess: (_, variables) => {
      setSavingItemId(undefined);
      const list = allBuyerLists?.all.find((l) => l.id === variables.id);
      const bulkSave = variables.buyerIds.length > 1;
      logEvent(bulkSave ? EventNames.buyerBulkSave : EventNames.buyerSaved, {
        buyerGuids: variables.buyerIds,
        buyerListName: list?.name,
        buyerListGuid: variables.id,
      });
      const messageText = bulkSave
        ? pluralise(variables.buyerIds.length, "buyer", undefined)
        : "Buyer";
      toast.message(`${messageText} saved to ${list?.name}`, {
        action: <ToastAction objectType="list" url={`${routes.buyerLists}/${variables.id}`} />,
      });
    },
    onError: (_, variables) => {
      setSavingItemId(undefined);
      const list = allBuyerLists?.all.find((l) => l.id === variables.id);
      toast.error(`Failed to save buyer to ${list?.name ?? "list"}`);
    },
  });

  const { mutate: removeBuyerFromList } = useRemoveBuyerEntries({
    onSuccess: (_, variables) => {
      setSavingItemId(undefined);
      const list = allBuyerLists?.all.find((l) => l.id === variables.id);
      const messageText =
        variables.buyerIds.length > 1
          ? pluralise(variables.buyerIds.length, "buyer", undefined)
          : "Buyer";
      toast.message(`${messageText} removed from ${list?.name}`, {
        action: <ToastAction objectType="list" url={`${routes.buyerLists}/${variables.id}`} />,
      });
    },
    onError: (_, variables) => {
      setSavingItemId(undefined);
      const list = allBuyerLists?.all.find((l) => l.id === variables.id);
      toast.error(`Failed to remove buyer from ${list?.name ?? "list"}`);
    },
  });

  const allItems = mapToDisplayItems(allBuyerLists, buyerIds);
  const filteredItems = filterBySearchText(searchText, allItems);

  const onItemClick = (id: string, isSaved: boolean) => {
    const clickedList = allBuyerLists?.all.find((list) => list.id === id);
    if (!clickedList) {
      return;
    }

    setSavingItemId(id);

    if (isSaved) {
      removeBuyerFromList({ id, buyerIds });
    } else {
      addBuyerToList({ id, buyerIds });
    }
  };

  return (
    <SaveToList
      items={filteredItems}
      loading={{
        lists: isLoading,
        itemId: savingItemId,
      }}
      isError={isError}
      onItemClick={onItemClick}
      entityIds={buyerIds}
      entityType="buyer"
      onSearch={(text) => setSearchText(text)}
      CreateListForm={CreateBuyerListFormWrapper}
      isCreateFormOpen={isCreateFormOpen}
      onToggleCreateForm={onToggleCreateForm}
    />
  );
}

function mapToDisplayItems(
  allBuyerLists: CategorizedBuyerLists | undefined,
  buyerIds: string[],
): Item[] {
  return (
    allBuyerLists?.all.map((buyerList: BuyerList) => ({
      id: buyerList.id,
      label: buyerList.name,
      // Saved only when every selected buyer is in the list, so a mixed selection offers to
      // save the rest rather than unsaving the one that is already there.
      isSaved:
        buyerIds.length > 0 &&
        isArrayInArray(
          buyerIds,
          buyerList.entries.map((entry) => entry.id),
        ),
      viewUrl: `${routes.buyerLists}/${buyerList.id}`,
    })) ?? []
  );
}

function filterBySearchText(searchText: string, allListItems: Item[]) {
  return searchText
    ? allListItems.filter((item) => item.label.toLowerCase().includes(searchText.toLowerCase()))
    : allListItems;
}

function CreateBuyerListFormWrapper(props: CreateListFormProps) {
  const { mutate } = useCreateBuyerList("Save drawer", {
    onSuccess: (data) => {
      toast.message(`Buyer saved to ${data.createBuyerList.name}`, {
        action: (
          <ToastAction objectType="list" url={`${routes.buyerLists}/${data.createBuyerList.id}`} />
        ),
      });
      props.onSuccess();
    },
    onError: () =>
      toast.error(
        "Unable to create your buyer list. Please contact customer support if the problem persists",
      ),
  });

  return (
    <CreateBuyerListForm
      onSubmit={(values) =>
        mutate({
          name: values.name,
          sharingType: values.sharingType as BuyerListSharingTypeEnum,
          buyerIds: values.buyerGuids,
        })
      }
      entityIds={props.entityIds}
      footer={props.footer}
    />
  );
}
